import type { SalaryPeriod, SalaryValue } from '../../../types';

type Bucket = Array<number | null>;

const STEP_BY_PERIOD = {
  hour: 1,
  day: 10,
  shift: 50,
  week: 50,
  month: 100,
  year: 5000
} as const;

const MODE_DOMINANCE_THRESHOLD = 0.8;
const MIN_ANCHORS_FOR_SIMPLE_SPLIT = 3;
const ANCHOR_COUNT_THRESHOLD_FOR_MORE_BUCKETS = 8;

const TERCILE_PROBABILITIES = [0, 1 / 3, 2 / 3, 1];
const QUARTILE_PLUS_PROBABILITIES = [0, 0.25, 0.5, 0.75, 0.95];

function toIntervalValue(salary: SalaryValue) {
  switch (salary.type) {
    case 'fixed':
      return [salary.amount, salary.amount];
    case 'range':
      return [salary.minSalary, salary.maxSalary];
    case 'from':
      return [salary.amount, Number.POSITIVE_INFINITY];
    case 'upto':
      return [0, salary.amount];
    default:
      throw new Error('Unknown salary type');
  }
}

const roundDownToStep = (x: number, step: number) =>
  Math.floor(x / step) * step;

const roundUpToStep = (x: number, step: number) => Math.ceil(x / step) * step;

function getQuantiles(sortedNumbers: number[], probabilities: number[]) {
  const arr = [...sortedNumbers].sort((a, b) => a - b);
  const n = arr.length;
  if (!n) {
    return probabilities.map(() => 0);
  }

  return probabilities.map((prob) => {
    const pos = prob * (n - 1); // позиция в [0, n-1]
    const lower = Math.floor(pos);
    const upper = Math.ceil(pos);

    if (lower === upper) {
      return arr[lower];
    }
    const weight = pos - lower;
    return arr[lower] + weight * (arr[upper] - arr[lower]); // линейная интерполяция
  });
}

/**
 * Округляет сырые интервалы до шага step, гарантируя минимальную ширину step для конечного интервала.
 */
function roundBuckets(rawBuckets: Bucket[], step: number) {
  return rawBuckets.map(([low, high]) => {
    const lowRounded =
      low == null || !isFinite(low) ? null : roundDownToStep(low, step);
    let highRounded =
      high == null || !isFinite(high) ? null : roundUpToStep(high, step);

    const hasInvalidOrZeroWidth =
      lowRounded != null && highRounded != null && lowRounded >= highRounded;

    if (hasInvalidOrZeroWidth) {
      highRounded = lowRounded + step;
    }

    return [lowRounded, highRounded];
  });
}

/**
 * Сливает слишком узкие соседние бакеты (ширина < minWidth) в предыдущий.
 */
function mergeTinyBuckets(buckets: Bucket[], minWidth: number) {
  const out: Bucket[] = [];

  buckets.forEach((bucket) => {
    if (!out.length) {
      out.push(bucket);
      return;
    }

    const [low, high] = bucket;
    const [prevLow] = out[out.length - 1];

    const width = low == null || high == null ? null : high - low;

    if (width != null && width < minWidth) {
      // расширяем предыдущий бакет верхней границей текущего
      out[out.length - 1] = [prevLow, high];
    } else {
      out.push(bucket);
    }
  });

  return out;
}

/**
 * Соединяет "стык-в-стык" бакеты: если следующий начинается там, где закончился предыдущий,
 * корректируем нижнюю границу следующего на prevHigh. Если предыдущий открыт до +∞ — отбрасываем последующие.
 */
function buttJoinBuckets(buckets: Bucket[]) {
  const out: Bucket[] = [];

  buckets.forEach(([low, high]) => {
    if (!out.length) {
      out.push([low, high]);
      return;
    }

    const lastOut = out[out.length - 1];
    const prevHigh = lastOut[1];

    if (prevHigh == null) {
      // предыдущий открыт до +∞ — последующие не нужны
      return;
    }

    const newLow = prevHigh;
    const newHigh = high;

    const hasInvalidWidth =
      newHigh != null && newLow != null && newLow >= newHigh;
    if (hasInvalidWidth) {
      return;
    }

    out.push([newLow, newHigh]);
  });

  return out;
}

/**
 * Приводит набор "сырых" бакетов к финальному виду: округляет, сливает узкие, стыкует.
 */
function tidyBuckets(rawBuckets: Bucket[], step: number) {
  const rounded = roundBuckets(rawBuckets, step);
  const merged = mergeTinyBuckets(rounded, step);
  return buttJoinBuckets(merged);
}

function isFiniteNumber(value: number | null): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Проверяет, пересекается ли интервал bucket с любым из интервалов в intervals.
 */
function intervalHasAnyData(bucket: Bucket, intervals: Bucket[]) {
  const [bLow, bHigh] = bucket;

  const bucketLow = bLow ?? Number.NEGATIVE_INFINITY;
  const bucketHigh = bHigh ?? Number.POSITIVE_INFINITY;

  return intervals.some(([iLow, iHigh]) => {
    const intervalLow = iLow ?? Number.NEGATIVE_INFINITY;
    const intervalHigh = iHigh ?? Number.POSITIVE_INFINITY;

    return intervalLow <= bucketHigh && intervalHigh >= bucketLow;
  });
}

/**
 * Убирает пустые интервалы и затем стыкует соседние.
 */
function removeEmptyIntervals(buckets: Bucket[], intervals: Bucket[]) {
  const filtered = buckets.filter((b) => intervalHasAnyData(b, intervals));
  return filtered.length ? buttJoinBuckets(filtered) : [];
}

/**
 * Округление якоря для вычисления моды: если вниз округление даёт 0 при положительном значении — округляем вверх.
 */
function roundAnchorForMode(anchor: number, step: number) {
  const down = roundDownToStep(anchor, step);
  if (down === 0 && anchor > 0) {
    return roundUpToStep(anchor, step);
  }
  return down;
}

export function buildSalaryBuckets(
  salaries: SalaryValue[],
  period: SalaryPeriod
) {
  const step = STEP_BY_PERIOD[period];

  const intervals: Bucket[] = salaries.map(toIntervalValue);

  // конечные якорные точки
  const finiteMins = intervals.map(([low]) => low).filter(isFiniteNumber);
  const finiteMaxs = intervals.map(([, high]) => high).filter(isFiniteNumber);

  const uniqueAnchors = [...new Set([...finiteMins, ...finiteMaxs])].sort(
    (a, b) => a - b
  );

  const anchorCount = uniqueAnchors.length;

  // подсчёт доминирующей моды по округлённым якорям
  const roundedAnchorPoints = [...finiteMins, ...finiteMaxs].map((anchor) =>
    roundAnchorForMode(anchor, step)
  );

  const frequencyMap = new Map<number, number>();
  roundedAnchorPoints.forEach((point) => {
    frequencyMap.set(point, (frequencyMap.get(point) || 0) + 1);
  });

  let dominantAnchor: number | null = null;
  let dominantShare = 0;

  if (frequencyMap.size > 0) {
    let totalCount = 0;
    let maxCount = 0;

    frequencyMap.entries().forEach(([anchorValue, count]) => {
      totalCount += count;
      if (count > maxCount) {
        maxCount = count;
        dominantAnchor = anchorValue;
      }
    });

    if (totalCount > 0) {
      dominantShare = maxCount / totalCount;
    }
  }

  const tidyAndClean = (rawBuckets: Bucket[]) => {
    const tidy = tidyBuckets(rawBuckets, step);
    const cleaned = removeEmptyIntervals(tidy, intervals);

    if (!cleaned.length) {
      return tidy.map(([low, high]) => ({ low, high }));
    }
    return cleaned.map(([low, high]) => ({ low, high }));
  };

  // МАЛО ДАННЫХ — возвращаем два бакета вокруг моды
  if (anchorCount < MIN_ANCHORS_FOR_SIMPLE_SPLIT) {
    if (dominantAnchor == null) {
      return [];
    }

    const buckets: Bucket[] = [
      [null, dominantAnchor],
      [dominantAnchor, null]
    ];

    return tidyAndClean(buckets);
  }

  // ОДНА ТОЧКА ДАВИТ (>= threshold)
  if (dominantShare >= MODE_DOMINANCE_THRESHOLD && dominantAnchor != null) {
    const buckets: Bucket[] = [
      [null, dominantAnchor],
      [dominantAnchor, null]
    ];

    return tidyAndClean(buckets);
  }

  // Выбираем стратегию разбивки
  let resultBuckets: Bucket[];

  if (anchorCount < ANCHOR_COUNT_THRESHOLD_FOR_MORE_BUCKETS) {
    // 3 корзины по терцилям (~0, 1/3, 2/3, 1 — последняя открытая)
    const quantiles = getQuantiles(uniqueAnchors, TERCILE_PROBABILITIES);

    resultBuckets = [
      [quantiles[0], quantiles[1]],
      [quantiles[1], quantiles[2]],
      [quantiles[2], null]
    ];
  } else {
    // 4 корзины: 0, .25, .5, .75, .95 (последняя открытая)
    const quantiles = getQuantiles(uniqueAnchors, QUARTILE_PLUS_PROBABILITIES);

    resultBuckets = [
      [quantiles[0], quantiles[1]],
      [quantiles[1], quantiles[2]],
      [quantiles[2], quantiles[3]],
      [quantiles[3], null]
    ];
  }

  const tidy = tidyBuckets(resultBuckets, step);
  const cleaned = removeEmptyIntervals(tidy, intervals);

  return cleaned.map(([low, high]) => ({ low, high }));
}
